import dayjs from "dayjs";

import React, { useCallback, useEffect, useRef, useState } from "react";

import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Box, Button, ButtonGroup, Grid, Typography } from "@mui/material";

import { CalendarItemType } from "@/typings";

import CalendarItem from "./CalendarItem";

interface CalendarProps {
  getItemData: (date: string) => CalendarItemType;
  onItemClick?: (data: CalendarItemType["points"][number]) => void;
  children?: React.ReactNode;
}

const Calendar = ({ getItemData, onItemClick, children }: CalendarProps) => {
  const defaultMonth = useRef(dayjs().format("YYYY-MM"));
  const minMonth = useRef(dayjs().format("YYYY-MM"));
  const maxMonth = useRef(dayjs().add(12, "month").format("YYYY-MM"));

  const [month, setMonth] = useState(defaultMonth.current);
  const [dateList, setDateList] = useState<string[]>([]);

  const handleMonthChange = useCallback((count: number) => {
    setMonth((prev) => {
      return dayjs(prev).add(count, "month").format("YYYY-MM");
    });
  }, []);

  const getDateList = useCallback((month: string) => {
    const monthStartDate = dayjs(month).format("YYYY-MM-DD");
    const calendarStartDate = dayjs(monthStartDate).startOf("week").format("YYYY-MM-DD");

    // 5 weeks
    const dayCount = 6 * 7;
    const dateList = [];

    for (let i = 0; i < dayCount; i++) {
      const date = dayjs(calendarStartDate).add(i, "day").format("YYYY-MM-DD");
      dateList.push(date);
    }

    return dateList;
  }, []);

  useEffect(() => {
    const dateList = getDateList(month);

    setDateList(dateList);
  }, [month, getDateList]);

  return (
    <Box>
      {children}

      <Box className="flex items-center">
        <ButtonGroup variant="contained" size="small">
          <Button disabled={month === minMonth.current} onClick={() => handleMonthChange(-1)}>
            <ChevronLeft />
          </Button>
          <Button disabled={month === maxMonth.current} onClick={() => handleMonthChange(1)}>
            <ChevronRight />
          </Button>
        </ButtonGroup>

        <Button
          size="small"
          variant="contained"
          className="!ml-[15px]"
          onClick={() => setMonth(defaultMonth.current)}
          disabled={month === defaultMonth.current}
        >
          Today
        </Button>

        <Box className="flex flex-1 items-center justify-center">
          <Typography color="textPrimary" className="!text-[22px] !font-bold">
            {dayjs(month).format("MMM YYYY")}
          </Typography>
        </Box>
      </Box>

      <Box
        className="sticky top-0 mt-[10px] z-[999]"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.mode === "dark" ? "#272727" : "#fff";
          },
        }}
      >
        <Grid container className="py-[10px]">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((ele) => {
            return (
              <Grid key={ele} item xs>
                <Typography color="textPrimary" variant="body1" className="text-center !font-bold">
                  {ele}
                </Typography>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <Grid container>
        {(dateList || []).map((date) => {
          const data = getItemData(date);

          return <CalendarItem key={date} data={data} month={month} onItemClick={onItemClick} />;
        })}
      </Grid>
    </Box>
  );
};

export default React.memo(Calendar);
